<script lang="ts">
	import DateFilter from './DateFilter.svelte';
	import GenreFilter from './GenreFilter.svelte';
	import type { GenreDataType } from '$lib/types/GenreData';
	import { slide } from 'svelte/transition';
	import ViewToggle from './ViewToggle.svelte';
	import { sharedConfig } from '$lib/animations';
	import FilterButton from './FilterButton.svelte';
	import { DateFormats } from '@tools';
	import { FollowScroll } from '@actions/FollowScroll';

	let expandedState = $state<'genre' | 'date' | 'none'>('none');

	let {
		selectedGenres = $bindable([]),
		from = $bindable(new Date()),
		view = $bindable('list')
	}: {
		selectedGenres: GenreDataType[];
		from: Date;
		view: 'list' | 'poster';
	} = $props();

	const removeGenre = (genre: GenreDataType) => {
		const index = selectedGenres.findIndex((g) => g.id === genre.id);
		selectedGenres.splice(index, 1);
	};

	const collapse = () => {
		expandedState = 'none';
	};

	const toggleGenre = () => {
		if (expandedState === 'genre') collapse();
		else expandedState = 'genre';
	};

	const toggleDate = () => {
		if (expandedState === 'date') collapse();
		else expandedState = 'date';
	};

	const removeFilters = () => {
		selectedGenres = [];
		from = new Date();
		collapse();
	};
</script>

<section>
	<div class="row">
		<FilterButton
			name="Genre"
			onclick={toggleGenre}
			active={expandedState === 'genre'}
			items={selectedGenres.map((g) => ({
				name: g.name,
				clear: () => removeGenre(g)
			}))}
		/>
		<FilterButton
			name="Dato"
			onclick={toggleDate}
			active={expandedState === 'date'}
			items={from > new Date()
				? [
						{
							name: DateFormats.basicDate(from.toISOString()),
							clear: () => (from = new Date())
						}
					]
				: undefined}
		/>
		<ViewToggle bind:view />
	</div>

	{#if expandedState !== 'none'}
		<div class="container" in:slide={sharedConfig}>
			{#if expandedState === 'genre'}
				<div
					class="inner"
					use:FollowScroll={{ delayedEnable: sharedConfig.duration, onClose: collapse }}
					in:slide={sharedConfig}
				>
					<GenreFilter bind:selectedGenres />
					<div class="buttons">
						<button class="button:pill no-icon" onclick={collapse}>Vis resultat</button>
						<button class="button:outline no-icon" onclick={removeFilters}>Nulstil filter</button>
					</div>
				</div>
			{:else if expandedState === 'date'}
				<div
					class="inner"
					use:FollowScroll={{ delayedEnable: sharedConfig.duration, onClose: collapse }}
					in:slide={sharedConfig}
				>
					<DateFilter bind:from />
					<div class="buttons">
						<button class="button:pill no-icon" onclick={collapse}>Vis resultat</button>
						<button class="button:outline no-icon" onclick={removeFilters}>Nulstil filter</button>
					</div>
				</div>
			{/if}
		</div>
	{/if}
</section>

<style lang="scss">
	section {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		position: sticky;
		top: var(--nav-height);
		z-index: 6;
	}

	.row {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: start;
		gap: $gap4;
		height: var(--nav-height);
		padding: 0 var(--gutter);
		background-color: $colorBackground;
		width: 100%;

		:global([data-component='ViewToggle']) {
			margin-left: auto;
		}

		@include md {
			background-color: transparent;
			justify-content: end;
			width: auto;
			gap: $gap5;
			padding-left: 0;

			:global([data-component='ViewToggle']) {
				margin-left: 0;
			}
		}
	}

	.container {
		color: $colorDropdownForeground;
		width: 100%;
		display: flex;
		flex-direction: column;
		max-height: calc(var(--vh) - var(--nav-height) * 2);
		overflow-y: auto;

		@include md {
			max-height: unset;
		}
	}

	.inner {
		display: flex;
		flex-direction: column;
		background-color: $colorDropdownBackground;
	}

	.buttons {
		display: flex;
		flex-direction: row;
		justify-content: center;
		gap: $gap3;
		padding: $gap6 var(--gutter);
		border-top: 1px solid $colorBorder;

		@include md {
			display: none;
		}
	}
</style>
